/**
 * Single source of truth: Global Apps (top bar) and their Modules (sidebar).
 * Short paths for one.atw.net (no /dashboard prefix).
 */

import { financePurchasesChildLinks, financeSalesChildLinks } from "@/lib/financeNavConfig";
import { WORKSUITE_MODULES as WORKSUITE_MODULE_SPECS, workSuiteModuleHref } from "@/lib/worksuiteConfig";

export type GlobalAppSlug =
  | "home"
  | "crm"
  | "finance"
  | "human-resources"
  | "marketing"
  | "knowledge"
  | "projects"
  | "worksuite"
  | "analytics"
  | "custom-apps"
  | "support"
  | "platform";

export type DashboardModule = {
  name: string;
  href: string;
  children?: DashboardModule[];
  group?: boolean;
  groupExpandPrefixes?: string[];
  comingSoon?: boolean;
};

export type DashboardAppDefinition = {
  slug: GlobalAppSlug;
  label: string;
  topNavHref: string;
  pathPrefixes: string[];
  modules: DashboardModule[];
};

const CRM_MODULES: DashboardModule[] = [
  { name: "Dashboard", href: "/crm/overview" },
  { name: "Accounts", href: "/crm/accounts" },
  { name: "Leads", href: "/crm/leads" },
  { name: "Deals", href: "/crm/deals" },
  { name: "Contacts", href: "/crm/contacts" },
  { name: "Import", href: "/crm/import" },
];

const MARKETING_MODULES: DashboardModule[] = [
  { name: "Overview", href: "/marketing" },
  { name: "Campaigns", href: "/marketing/campaigns" },
  {
    name: "Blogs",
    href: "/blogs",
    group: true,
    groupExpandPrefixes: ["/blogs/posts", "/blogs/analytics", "/blogs/comments"],
    children: [
      { name: "Posts", href: "/blogs/posts" },
      { name: "Analytics", href: "/blogs/analytics" },
      { name: "Comments", href: "/blogs/comments" },
    ],
  },
  { name: "Socials", href: "/marketing/socials", comingSoon: true },
];

/** Knowledge base overview: New knowledge base + switcher. Child modules follow the active base. */
const KNOWLEDGE_MODULES: DashboardModule[] = [
  { name: "Knowledge base", href: "/knowledge" },
  { name: "Libraries", href: "/knowledge/libraries" },
  { name: "Books", href: "/knowledge/books" },
  { name: "Articles", href: "/knowledge/articles" },
];

const PROJECTS_MODULES: DashboardModule[] = [
  { name: "Overview", href: "/projects" },
  { name: "Projects", href: "/projects/list" },
  { name: "Tasks", href: "/projects/tasks" },
  { name: "Issues", href: "/projects/issues" },
];

const WORKSUITE_MODULE_LINKS: DashboardModule[] = WORKSUITE_MODULE_SPECS.map((m) => ({
  name: m.title,
  href: workSuiteModuleHref(m.id),
}));

const WORKSUITE_APP_MODULES: DashboardModule[] = [
  { name: "Overview", href: "/worksuite" },
  { name: "Module index", href: "/worksuite/modules" },
  {
    name: "Integrations",
    href: "/worksuite/modules",
    group: true,
    groupExpandPrefixes: ["/worksuite/modules", ...WORKSUITE_MODULE_LINKS.map((c) => c.href)],
    children: WORKSUITE_MODULE_LINKS,
  },
];

const HOME_WORKSPACE_MODULES: DashboardModule[] = [{ name: "Dashboard overview", href: "/" }];

const HOME_MANAGEMENT_MODULES: DashboardModule[] = [
  { name: "Users", href: "/users" },
  { name: "Organization", href: "/organization" },
  { name: "Settings", href: "/settings" },
  { name: "Profiles", href: "/settings/profiles" },
  { name: "Groups", href: "/settings/groups" },
];

const HOME_QUICK_ACCESS_MODULES: DashboardModule[] = [
  { name: "CRM", href: "/crm/overview" },
  { name: "Finance", href: "/finance" },
  { name: "Projects", href: "/projects" },
  { name: "Knowledge", href: "/knowledge" },
  { name: "WorkSuite", href: "/worksuite" },
];

const HOME_MODULES: DashboardModule[] = [
  {
    name: "Workspace",
    href: "/home/workspace",
    group: true,
    groupExpandPrefixes: HOME_WORKSPACE_MODULES.map((m) => m.href),
    children: HOME_WORKSPACE_MODULES,
  },
  {
    name: "Management",
    href: "/home/management",
    group: true,
    groupExpandPrefixes: HOME_MANAGEMENT_MODULES.map((m) => m.href),
    children: HOME_MANAGEMENT_MODULES,
  },
  {
    name: "Quick access",
    href: "/home/quick-access",
    group: true,
    groupExpandPrefixes: HOME_QUICK_ACCESS_MODULES.map((m) => m.href),
    children: HOME_QUICK_ACCESS_MODULES,
  },
];

const FINANCE_MODULES: DashboardModule[] = [
  { name: "Home", href: "/finance" },
  { name: "Items", href: "/finance/items" },
  { name: "Banking", href: "/finance/banking", comingSoon: true },
  { name: "Time", href: "/finance/time", comingSoon: true },
  { name: "Accounting", href: "/finance/accounting", comingSoon: true },
  { name: "Taxes", href: "/finance/taxes", comingSoon: true },
  {
    name: "Sales",
    href: "/finance/sales",
    group: true,
    children: financeSalesChildLinks.map((l) => ({
      name: l.name,
      href: l.href,
      comingSoon: l.comingSoon,
    })),
  },
  {
    name: "Purchases",
    href: "/finance/purchases",
    group: true,
    children: financePurchasesChildLinks.map((l) => ({
      name: l.name,
      href: l.href,
      comingSoon: l.comingSoon,
    })),
  },
  { name: "Reports", href: "/finance/reports", comingSoon: true },
  { name: "Documents", href: "/finance/documents", comingSoon: true },
  { name: "Compliance", href: "/finance/compliance", comingSoon: true },
  { name: "Settings", href: "/finance/settings" },
];

export const DASHBOARD_APP_ORDER: GlobalAppSlug[] = [
  "home",
  "crm",
  "finance",
  "human-resources",
  "marketing",
  "knowledge",
  "projects",
  "worksuite",
  "analytics",
  "custom-apps",
  "support",
  "platform",
];

export const DASHBOARD_ENTITLEMENT_SLUGS: Exclude<GlobalAppSlug, "home">[] = [
  "crm",
  "finance",
  "human-resources",
  "marketing",
  "knowledge",
  "projects",
  "worksuite",
  "analytics",
  "custom-apps",
  "support",
  "platform",
];

export const CUSTOM_APPS_CREATE_MODULE_HREF = "/custom-apps/new";

export const DASHBOARD_APPS: DashboardAppDefinition[] = [
  {
    slug: "home",
    label: "Home",
    topNavHref: "/",
    pathPrefixes: [],
    modules: HOME_MODULES,
  },
  {
    slug: "crm",
    label: "CRM",
    topNavHref: "/crm/overview",
    pathPrefixes: ["/crm"],
    modules: CRM_MODULES,
  },
  {
    slug: "finance",
    label: "Finance",
    topNavHref: "/finance",
    pathPrefixes: ["/finance"],
    modules: FINANCE_MODULES,
  },
  {
    slug: "human-resources",
    label: "Human Resources",
    topNavHref: "/human-resources",
    pathPrefixes: ["/human-resources"],
    modules: [
      { name: "Directory", href: "/human-resources" },
      { name: "Settings", href: "/human-resources/settings" },
    ],
  },
  {
    slug: "marketing",
    label: "Marketing",
    topNavHref: "/marketing",
    pathPrefixes: ["/marketing", "/blogs"],
    modules: MARKETING_MODULES,
  },
  {
    slug: "knowledge",
    label: "Knowledge",
    topNavHref: "/knowledge",
    pathPrefixes: ["/knowledge"],
    modules: KNOWLEDGE_MODULES,
  },
  {
    slug: "projects",
    label: "Projects",
    topNavHref: "/projects",
    pathPrefixes: ["/projects"],
    modules: PROJECTS_MODULES,
  },
  {
    slug: "worksuite",
    label: "WorkSuite",
    topNavHref: "/worksuite",
    pathPrefixes: ["/worksuite"],
    modules: WORKSUITE_APP_MODULES,
  },
  {
    slug: "analytics",
    label: "Analytics",
    topNavHref: "/analytics/overview",
    pathPrefixes: ["/analytics"],
    modules: [
      { name: "Overview", href: "/analytics/overview" },
      { name: "Sites", href: "/analytics/sites" },
      { name: "Journeys", href: "/analytics/journeys" },
      { name: "Settings", href: "/analytics/settings" },
    ],
  },
  {
    slug: "custom-apps",
    label: "Custom apps",
    topNavHref: "/custom-apps",
    pathPrefixes: ["/custom-apps"],
    modules: [
      { name: "Overview", href: "/custom-apps" },
      { name: "New app", href: CUSTOM_APPS_CREATE_MODULE_HREF },
    ],
  },
  {
    slug: "support",
    label: "Support",
    topNavHref: "/support",
    pathPrefixes: ["/support"],
    modules: [{ name: "Tickets", href: "/support" }],
  },
  {
    slug: "platform",
    label: "Platform",
    topNavHref: "/platform",
    pathPrefixes: ["/platform"],
    modules: [
      { name: "Overview", href: "/platform" },
      { name: "AI Settings", href: "/platform/ai-settings" },
      { name: "Organizations", href: "/platform/organizations" },
      { name: "Payments & billing", href: "/platform/payments" },
    ],
  },
];

export const DASHBOARD_APP_BY_SLUG = Object.fromEntries(
  DASHBOARD_APPS.map((app) => [app.slug, app]),
) as Record<GlobalAppSlug, DashboardAppDefinition>;

function normalizePath(pathname: string): string {
  const raw = pathname.split("?")[0] || "/";
  if (raw.length > 1 && raw.endsWith("/")) return raw.slice(0, -1);
  return raw;
}

/** Routes that belong to Home in the shell (no global app prefix). */
function isHomeShellPath(p: string): boolean {
  if (p === "/" || p === "") return true;
  if (p === "/settings" || p.startsWith("/settings/")) return true;
  if (p === "/users" || p.startsWith("/users/")) return true;
  if (p === "/organization" || p.startsWith("/organization/")) return true;
  return false;
}

export function resolveDashboardApp(
  pathname: string,
  allowedSlugs: Set<GlobalAppSlug>,
): DashboardAppDefinition {
  const p = normalizePath(pathname);
  if (isHomeShellPath(p)) {
    return DASHBOARD_APP_BY_SLUG.home;
  }

  let best: DashboardAppDefinition | null = null;
  let bestLen = -1;

  for (const app of DASHBOARD_APPS) {
    if (!allowedSlugs.has(app.slug)) continue;
    for (const prefix of app.pathPrefixes) {
      if (p === prefix || p.startsWith(`${prefix}/`)) {
        if (prefix.length > bestLen) {
          bestLen = prefix.length;
          best = app;
        }
      }
    }
  }

  return best ?? DASHBOARD_APP_BY_SLUG.home;
}

export function topNavItemActive(pathname: string, slug: GlobalAppSlug, topNavHref: string): boolean {
  const p = normalizePath(pathname);
  if (slug === "home") {
    return p === "/";
  }
  if (slug === "marketing") {
    return p === "/marketing" || p.startsWith("/marketing/") || p.startsWith("/blogs");
  }
  if (slug === "crm") {
    return p === "/crm" || p.startsWith("/crm/");
  }
  if (slug === "worksuite") {
    return p === "/worksuite" || p.startsWith("/worksuite/");
  }
  return p === topNavHref || p.startsWith(`${topNavHref}/`);
}
